import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useNotifications } from "@/context/NotificationsContext";
import { PageHeader, Card, Loading, EmptyState, Btn } from "@/components/primitives";
import { fmtDate } from "@/lib/format";
import { MessageCircleMore, Search, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

// Delivery pill derived from Telnyx API status + carrier webhook delivery_status.
// Order matters — matches the backend team's recommended mapping exactly.
function pillFor(l) {
  const st = (l.status || "").toLowerCase();
  const ds = (l.delivery_status || "").toLowerCase();
  if (st === "failed") return { cat: "failed", label: "Failed", cls: "bg-curtron-terracotta/15 text-curtron-terracotta", dot: "bg-curtron-terracotta" };
  if (ds === "delivered") return { cat: "delivered", label: "Delivered", cls: "bg-curtron-green/15 text-curtron-green", dot: "bg-curtron-green" };
  if (ds === "delivery_failed") return { cat: "rejected", label: "Rejected", cls: "bg-curtron-terracotta/15 text-curtron-terracotta", dot: "bg-curtron-terracotta" };
  return { cat: "pending", label: "Pending", cls: "bg-curtron-ochre/15 text-curtron-ochre", dot: "bg-curtron-ochre" };
}

const CHIPS = [["all", "All"], ["delivered", "Delivered"], ["rejected", "Rejected"], ["pending", "Pending"], ["failed", "Failed"]];

export default function SmsLogs() {
  const { subscribe } = useNotifications();
  const [logs, setLogs] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try { const r = await api.get("/sms/logs"); setLogs(r.data); }
    catch { setLogs([]); }
    finally { setRefreshing(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live carrier delivery updates: patch the matching row (or reload if unseen).
  useEffect(() => {
    const off = subscribe((evt) => {
      if (evt.type !== "sms.delivery") return;
      setLogs((prev) => {
        if (!prev) return prev;
        const idx = prev.findIndex((l) => (evt.log_id && l.id === evt.log_id) || (evt.telnyx_id && l.telnyx_id === evt.telnyx_id));
        if (idx === -1) { load(); return prev; }
        const copy = [...prev];
        copy[idx] = { ...copy[idx], delivery_status: evt.delivery_status };
        return copy;
      });
    });
    return off;
  }, [subscribe, load]);

  const counts = useMemo(() => {
    const c = { delivered: 0, rejected: 0, pending: 0, failed: 0 };
    (logs || []).forEach((l) => { c[pillFor(l).cat]++; });
    return c;
  }, [logs]);

  const filtered = useMemo(() => {
    if (!logs) return [];
    const t = q.trim().toLowerCase();
    let list = tab === "all" ? logs : logs.filter((l) => pillFor(l).cat === tab);
    if (t) list = list.filter((l) => [l.to, l.from, l.text, l.kind, l.status].filter(Boolean).some((v) => String(v).toLowerCase().includes(t)));
    return [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [logs, q, tab]);

  if (logs === null) return <Loading />;

  return (
    <div>
      <PageHeader title="SMS Logs" subtitle="Delivery audit trail for dispatch alerts.">
        <Btn variant="secondary" onClick={load} data-testid="sms-refresh-btn"><RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh</Btn>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Card className="p-3 text-center"><div className="font-heading text-2xl font-bold text-curtron-green">{counts.delivered}</div><div className="text-[11px] uppercase text-curtron-muted">Delivered</div></Card>
        <Card className="p-3 text-center"><div className="font-heading text-2xl font-bold text-curtron-ochre">{counts.pending}</div><div className="text-[11px] uppercase text-curtron-muted">Pending</div></Card>
        <Card className="p-3 text-center"><div className="font-heading text-2xl font-bold text-curtron-terracotta">{counts.rejected}</div><div className="text-[11px] uppercase text-curtron-muted">Rejected</div></Card>
        <Card className="p-3 text-center"><div className="font-heading text-2xl font-bold text-curtron-terracotta">{counts.failed}</div><div className="text-[11px] uppercase text-curtron-muted">Failed</div></Card>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-3">
        {CHIPS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`sms-tab-${key}`}
            className={`shrink-0 h-8 px-3 rounded-full text-sm font-medium border transition-colors ${tab === key ? "bg-curtron-green text-white border-curtron-green" : "bg-curtron-surface text-curtron-sub border-curtron-line hover:bg-curtron-bg"}`}>
            {label}{key !== "all" && counts[key] ? ` (${counts[key]})` : ""}
          </button>
        ))}
      </div>

      <Card className="p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-curtron-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="sms-search" placeholder="Search number, message…"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-curtron-line bg-curtron-bg text-sm outline-none focus:border-curtron-green focus:bg-curtron-surface transition-colors" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card><EmptyState icon={MessageCircleMore} title="No SMS logs" /></Card>
      ) : (
        <Card className="divide-y divide-curtron-line">
          {filtered.map((l) => {
            const p = pillFor(l);
            return (
              <div key={l.id} className="px-4 py-3.5 flex gap-3" data-testid={`sms-row-${l.id}`}>
                <span className={cn("w-2 h-2 rounded-full mt-1.5 shrink-0", p.dot)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-curtron-ink truncate">To {l.to}</span>
                    <span className={cn("text-[10px] uppercase font-semibold tracking-wide rounded-full px-2 py-0.5 shrink-0", p.cls)} data-testid={`sms-status-${l.id}`}>{p.label}</span>
                  </div>
                  <p className="text-sm text-curtron-sub mt-1 whitespace-pre-wrap line-clamp-3">{l.text}</p>
                  {l.error && <div className="text-xs text-curtron-terracotta mt-1">{l.error}</div>}
                  <div className="text-[11px] text-curtron-muted mt-1">{l.kind ? `${l.kind} · ` : ""}{fmtDate(l.created_at)}</div>
                </div>
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
